import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Tag, Select, Space, message, Drawer, Button,
} from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { pujaService } from '../../services/api';
import TicketHilo from '../../components/TicketHilo';
import dayjs from 'dayjs';

const { Option } = Select;

const estadoColor    = { abierto: 'blue', en_proceso: 'orange', resuelto: 'green', cerrado: 'default' };
const prioridadColor = { baja: 'default', normal: 'blue', alta: 'orange', urgente: 'red' };
const ESTADOS    = ['abierto', 'en_proceso', 'resuelto', 'cerrado'];
const PRIORIDADES = ['baja', 'normal', 'alta', 'urgente'];

const PujaTickets = () => {
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [filtros, setFiltros] = useState({});

  // Drawer detalle
  const [ticketActivo, setTicketActivo] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [mensajeText, setMensajeText] = useState('');
  const [enviando, setEnviando] = useState(false);

  const fetchTickets = useCallback(async (f = {}) => {
    setLoading(true);
    try {
      const res = await pujaService.getTickets(f);
      setTickets(res.data || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets(filtros);
  }, [filtros, fetchTickets]);

  const abrirDetalle = async (id) => {
    setLoadingDetalle(true);
    try {
      const res = await pujaService.getTicket(id);
      setTicketActivo(res.data);
      setMensajeText('');
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar ticket');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const enviarMensaje = async () => {
    if (!mensajeText.trim() || !ticketActivo) return;
    setEnviando(true);
    try {
      await pujaService.responder(ticketActivo.id_ticket, mensajeText);
      const res = await pujaService.getTicket(ticketActivo.id_ticket);
      setTicketActivo(res.data);
      setMensajeText('');
      fetchTickets(filtros);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al enviar mensaje');
    } finally {
      setEnviando(false);
    }
  };

  const actualizarTicket = async (campo, valor) => {
    if (!ticketActivo) return;
    try {
      await pujaService.actualizar(ticketActivo.id_ticket, { [campo]: valor });
      setTicketActivo(prev => ({ ...prev, [campo]: valor }));
      fetchTickets(filtros);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al actualizar ticket');
    }
  };

  const columnas = [
    {
      title: 'Asunto',
      dataIndex: 'asunto',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      width: 130,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'Origen',
      key: 'origen',
      width: 160,
      render: (_, r) => (
        <span>
          {r.usuario?.nom_usuario || `#${r.id_usuario}`}
          {r.cliente?.razon_social && (
            <span style={{ color: '#999', fontSize: 11, display: 'block' }}>
              {r.cliente.razon_social}
            </span>
          )}
        </span>
      ),
    },
    {
      title: 'Prioridad',
      dataIndex: 'prioridad',
      width: 100,
      render: (v) => <Tag color={prioridadColor[v] || 'blue'}>{v || 'normal'}</Tag>,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 110,
      render: (v) => <Tag color={estadoColor[v] || 'default'}>{v}</Tag>,
    },
    {
      title: 'Fecha',
      dataIndex: 'updated_at',
      width: 130,
      render: (v) => dayjs(v).format('DD/MM/YY HH:mm'),
    },
    {
      title: '',
      key: 'accion',
      width: 80,
      render: (_, r) => (
        <Button
          size="small"
          icon={<MessageOutlined />}
          onClick={() => abrirDetalle(r.id_ticket)}
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Consultas y Reclamos — Soporte Puja</h2>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Estado"
          allowClear
          style={{ width: 130 }}
          onChange={(v) => setFiltros(f => ({ ...f, estado: v }))}
        >
          {ESTADOS.map(e => <Option key={e} value={e}>{e}</Option>)}
        </Select>
        <Select
          placeholder="Prioridad"
          allowClear
          style={{ width: 130 }}
          onChange={(v) => setFiltros(f => ({ ...f, prioridad: v }))}
        >
          {PRIORIDADES.map(p => (
            <Option key={p} value={p}>
              <Tag color={prioridadColor[p]}>{p}</Tag>
            </Option>
          ))}
        </Select>
      </Space>

      <Table
        scroll={{ x: 'max-content' }}
        dataSource={tickets}
        columns={columnas}
        rowKey="id_ticket"
        loading={loading}
        pagination={{ pageSize: 15 }}
        size="small"
      />

      <Drawer
        title={ticketActivo?.asunto || 'Detalle'}
        open={!!ticketActivo}
        onClose={() => setTicketActivo(null)}
        width={560}
        loading={loadingDetalle}
        extra={
          ticketActivo && (
            <Space>
              <Select
                value={ticketActivo.prioridad}
                onChange={(v) => actualizarTicket('prioridad', v)}
                style={{ width: 110 }}
                size="small"
              >
                {PRIORIDADES.map(p => (
                  <Option key={p} value={p}>
                    <Tag color={prioridadColor[p]}>{p}</Tag>
                  </Option>
                ))}
              </Select>
              <Select
                value={ticketActivo.estado}
                onChange={(v) => actualizarTicket('estado', v)}
                style={{ width: 120 }}
                size="small"
              >
                {ESTADOS.map(e => <Option key={e} value={e}>{e}</Option>)}
              </Select>
            </Space>
          )
        }
      >
        {ticketActivo && (
          <TicketHilo
            ticket={ticketActivo}
            mensajeText={mensajeText}
            onMensajeChange={setMensajeText}
            onEnviar={enviarMensaje}
            enviando={enviando}
          />
        )}
      </Drawer>
    </div>
  );
};

export default PujaTickets;
